import { Editor, Operation } from 'slate';

import { getRandomId } from 'craft/utils';

const fieldForOperation = {
  insert_node: 'node',
  merge_node: 'properties',
  split_node: 'properties',
};

function isValidOperation(operationType: string): operationType is keyof typeof fieldForOperation {
  return operationType in fieldForOperation;
}

const applyIdToProperty = (property: any, idKey: string, idGenerator: () => string) => {
  const { children } = property;

  return {
    ...property,
    id: idGenerator(),
    // We need to generate a new id for the children (if any)
    // Otherwise this causes issue with copy-paste (TEXT-316, TEXT-439)
    ...(children
      ? { children: children.map((child: any) => applyIdToProperty(child, idKey, idGenerator)) }
      : {}),
  };
};

export const withId = ({
  idKey = 'id',
  idGenerator = getRandomId,
}: {
  idKey?: string;
  idGenerator?: () => string;
} = {}) => <T extends Editor>(editor: T) => {
  const { apply } = editor;

  // eslint-disable-next-line no-param-reassign
  editor.apply = (operation: Operation) => {
    const { type } = operation;
    if (!isValidOperation(type)) {
      return apply(operation);
    }

    const propertyToChange = fieldForOperation[type];
    const op = operation[propertyToChange as keyof Operation];

    return apply({
      ...operation,
      [propertyToChange]: applyIdToProperty(op, idKey, idGenerator),
    });
  };

  return editor;
};
